import React, { useContext, useMemo, useState } from 'react';
import { Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import MissingToolMappingsContext from '@totem/components/salesOrders/missingToolMappings/MissingToolMappingsContext';
import { MissingToolMappingItem } from '@totem/components/salesOrders/missingToolMappings/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

const MissingToolMappingsTable = () => {
  const { loading, reportData, searchText } = useContext(
    MissingToolMappingsContext,
  );
  const [pageSize, setPageSize] = useState<number>(10);

  const filteredData = useMemo(() => {
    if (!searchText) {
      return reportData ?? [];
    }
    const lower = searchText.toLowerCase();
    return (reportData ?? []).filter((item) =>
      (item.salesOrder.number ?? '').toLowerCase().includes(lower),
    );
  }, [reportData, searchText]);

  const organizationFilters = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of reportData ?? []) {
      if (item.organization?.id) {
        seen.set(item.organization.id, item.organization.name);
      }
    }
    return Array.from(seen.entries())
      .map(([val, txt]) => ({ text: txt, value: val }))
      .sort((compA, compB) => compA.text.localeCompare(compB.text));
  }, [reportData]);

  const buildingFilters = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of reportData ?? []) {
      if (item.building?.id) {
        seen.set(item.building.id, item.building.name);
      }
    }
    return Array.from(seen.entries())
      .map(([val, txt]) => ({ text: txt, value: val }))
      .sort((compA, compB) => compA.text.localeCompare(compB.text));
  }, [reportData]);

  const serviceFilters = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of reportData ?? []) {
      if (item.service?.name) {
        seen.set(item.service.name, item.service.displayName);
      }
    }
    return Array.from(seen.entries())
      .map(([val, txt]) => ({ text: txt, value: val }))
      .sort((compA, compB) => compA.text.localeCompare(compB.text));
  }, [reportData]);

  const mappingFilters = useMemo(() => {
    const seen = new Set<string>();
    for (const item of reportData ?? []) {
      if (item.service?.requiredMapping) {
        seen.add(item.service.requiredMapping);
      }
    }
    return Array.from(seen)
      .map((val) => ({ text: val, value: val }))
      .sort((compA, compB) => compA.text.localeCompare(compB.text));
  }, [reportData]);

  const columns: ColumnProps<MissingToolMappingItem>[] = [
    {
      title: 'Sales Order',
      key: 'salesOrder',
      sorter: (compA, compB) =>
        (compA.salesOrder.number ?? '').localeCompare(
          compB.salesOrder.number ?? '',
        ),
      render: (_, record) => <span>{record.salesOrder.number}</span>,
    },
    {
      title: 'Line',
      key: 'line',
      width: 70,
      render: (_, record) => <span>{record.salesOrder.line}</span>,
    },
    {
      title: 'Service',
      key: 'service',
      filters: serviceFilters,
      onFilter: (value, record) => record.service?.name === value,
      sorter: (compA, compB) =>
        (compA.service.displayName ?? '').localeCompare(
          compB.service.displayName ?? '',
        ),
      render: (_, record) => <span>{record.service.displayName}</span>,
    },
    {
      title: 'Required Mapping',
      key: 'requiredMapping',
      filters: mappingFilters,
      onFilter: (value, record) => record.service?.requiredMapping === value,
      sorter: (compA, compB) =>
        (compA.service.requiredMapping ?? '').localeCompare(
          compB.service.requiredMapping ?? '',
        ),
      render: (_, record) => (
        <Tag color="purple">{record.service.requiredMapping}</Tag>
      ),
    },
    {
      title: 'Organization',
      key: 'organization',
      filters: organizationFilters,
      onFilter: (value, record) => record.organization?.id === value,
      sorter: (compA, compB) =>
        (compA.organization?.name ?? '').localeCompare(
          compB.organization?.name ?? '',
        ),
      render: (_, record) => <span>{record.organization?.name ?? ''}</span>,
    },
    {
      title: 'Building',
      key: 'building',
      filters: buildingFilters,
      onFilter: (value, record) => record.building?.id === value,
      sorter: (compA, compB) =>
        (compA.building?.name ?? '').localeCompare(compB.building?.name ?? ''),
      render: (_, record) => <span>{record.building?.name ?? ''}</span>,
    },
  ];

  return (
    <IBTable
      rowKey={(record) => record.id}
      columns={columns}
      dataSource={filteredData}
      loading={loading}
      pagination={{
        pageSize,
        showSizeChanger: true,
        onShowSizeChange: (_, size) => setPageSize(size),
      }}
    />
  );
};

export default MissingToolMappingsTable;
